import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, CalendarCheck, Video } from 'lucide-react';
import { MENTOR_ONBOARDING } from '@/constants/testIds';

export default function MentorOnboardingPage() {
    const { mentorId } = useParams();
    const [mentor, setMentor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const [status, setStatus] = useState({ connected: false });

    useEffect(() => {
        (async () => {
            try {
                const [m, s] = await Promise.all([
                    api.get(`/mentors/${mentorId}`),
                    api.get(`/mentor/${mentorId}/connection-status`),
                ]);
                setMentor(m.data);
                setStatus(s.data);
            } catch (e) {
                setMentor(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [mentorId]);

    const handleConnect = async () => {
        setConnecting(true);
        try {
            const { data } = await api.get(`/mentor/${mentorId}/oauth-url`);
            window.location.href = data.authorization_url;
        } catch (e) {
            toast.error('Failed to start Google connection. Please try again.');
            setConnecting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!mentor) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Card className="max-w-md">
                    <CardContent className="p-8 text-center">
                        <h1 className="font-display text-xl font-semibold">Mentor not found</h1>
                        <p className="mt-2 text-sm text-muted-foreground">This onboarding link is invalid.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen bg-noise bg-hero-soft">
            <div className="pointer-events-none absolute -top-24 -right-20 h-96 w-96 rounded-full bg-blob-sage blur-3xl" />
            <div className="relative mx-auto flex min-h-screen max-w-3xl items-center px-4 py-16 sm:px-6">
                <Card className="w-full border-border/60 shadow-lg">
                    <CardHeader>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <ShieldCheck className="h-4 w-4 text-primary" /> Secure onboarding link
                        </div>
                        <CardTitle className="font-display text-2xl">Welcome, {mentor.name.split(' ')[0]}!</CardTitle>
                        <p className="text-sm text-muted-foreground">
                            Connect your Google Calendar so students can book sessions with you based on your real availability. Sessions will be added to your calendar automatically with Google Meet included.
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            {[
                                { icon: CalendarCheck, title: 'Real availability', desc: 'We only show students slots when you’re free.' },
                                { icon: Video, title: 'Google Meet included', desc: 'Every booking auto-creates a Meet link.' },
                                { icon: ShieldCheck, title: 'You stay in control', desc: 'Disconnect anytime. We only read/write calendar events.' },
                            ].map((f, i) => (
                                <div key={i} className="rounded-lg border border-border/60 bg-card p-4">
                                    <f.icon className="h-5 w-5 text-primary" />
                                    <div className="mt-2 font-display text-sm font-semibold">{f.title}</div>
                                    <div className="text-xs text-muted-foreground">{f.desc}</div>
                                </div>
                            ))}
                        </div>

                        {status.connected ? (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">
                                <div className="flex items-center gap-2 font-semibold">
                                    <ShieldCheck className="h-5 w-5" /> Calendar connected!
                                </div>
                                <p className="mt-1">Students can now book sessions with you. You can re-connect below if you’d like to switch Google accounts.</p>
                            </div>
                        ) : (
                            <div className="rounded-lg border border-border/60 bg-accent/30 p-5 text-sm">
                                Click below to sign in with Google and grant Calendar access. You’ll return here when it’s done.
                            </div>
                        )}

                        <Button
                            size="lg"
                            className="w-full sm:w-auto"
                            data-testid={MENTOR_ONBOARDING.connect}
                            onClick={handleConnect}
                            disabled={connecting}
                        >
                            {connecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            {status.connected ? 'Re-connect Google Calendar' : 'Connect Google Calendar'}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
